/**
 * Capability Center public data contract (V1).
 *
 * This module is deliberately data-only. Each exported object has one strict
 * parser that is also the canonical builder: it validates unknown input and
 * returns a fresh, normalized object. It owns no persistence, execution,
 * projection, or work-state behavior.
 */
export const CAPABILITY_CENTER_CONTRACT_VERSION = 1;
export const CAPABILITY_ASSET_KIND_VALUES = [
    "skill",
    "agent",
    "workflow",
    "job_template",
];
export const CAPABILITY_DELIVERY_MODE_VALUES = ["local_editable", "remote_protected"];
export const CAPABILITY_DEPLOYMENT_VALUES = [
    "device",
    "shared_cloud",
    "private_runtime",
    "hybrid",
];
export const CAPABILITY_EXECUTION_KIND_VALUES = ["script", "model_call", "agent_turn"];
export const CAPABILITY_RELEASE_STATUS_VALUES = [
    "draft",
    "validating",
    "published",
    "unpublished",
    "revoked",
];
export const CAPABILITY_RIGHT_VALUES = [
    "discover",
    "use",
    "configure_input",
    "create_draft",
    "edit_draft",
    "review",
    "publish",
    "grant",
    "bind",
    "schedule",
    "view_implementation",
    "fork_export",
    "manage_connections",
    "manage_budget",
    "view_audit",
];
export const CAPABILITY_SOURCE_SCOPE_KIND_VALUES = ["platform", "organization", "user", "device"];
export const CAPABILITY_OWNER_KIND_VALUES = ["platform", "organization", "user"];
export const CAPABILITY_PRINCIPAL_KIND_VALUES = [
    "plan",
    "organization",
    "department",
    "role",
    "group",
    "user",
];
export const CAPABILITY_SUBJECT_KIND_VALUES = ["agent", "user", "device", "workspace", "runtime"];
export const CAPABILITY_GRANT_EFFECT_VALUES = ["allow", "deny"];
export const CAPABILITY_ASSIGNMENT_POLICY_VALUES = [
    "locked",
    "optional",
    "blocked",
    "invisible",
    "forbidden",
];
export const CAPABILITY_READINESS_STATUS_VALUES = ["ready", "blocked", "unknown"];
/** Conservative V1 wire bounds, expressed as UTF-16 string lengths and item counts. */
export const CAPABILITY_CENTER_LIMITS = Object.freeze({
    idLength: 256,
    kindLength: 64,
    nameLength: 120,
    descriptionLength: 2_048,
    summaryLength: 2_048,
    versionLength: 64,
    hashLength: 71,
    actionLength: 128,
    reasonCodeLength: 128,
    arrayItems: 64,
    /** Top-level catalog list size (observed+registered items after projection). */
    catalogListItems: 1_024,
    /** Distinct source summary rows on a catalog list response. */
    catalogSourceSummaries: 32,
    /** Maximum legacy source audience refs carried by one enterprise catalog item. */
    organizationAudienceRefsPerItem: 512,
    /** Maximum legacy source audience refs carried by one enterprise list/detail payload. */
    organizationAudienceRefsPerPayload: 4_096,
    errorCodeLength: 128,
    errorMessageLength: 512,
});
export const ORGANIZATION_CAPABILITY_BROWSER_ASSET_KIND_VALUES = [
    "skill",
    "agent",
    "job_template",
];
export const ORGANIZATION_CAPABILITY_BROWSER_SOURCE_KIND_VALUES = [
    "enterprise_skill",
    "enterprise_agent",
    "enterprise_task_definition",
];
export class CapabilityCenterContractError extends Error {
    path;
    constructor(path, message) {
        super(path ? `${path}: ${message}` : message);
        this.name = "CapabilityCenterContractError";
        this.path = path;
    }
}
const PROTOTYPE_KEYS = new Set(["__proto__", "prototype", "constructor"]);
const NORMALIZED_ISO_UTC_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const SHA256_DIGEST_RE = /^sha256:[0-9a-f]{64}$/;
const CAPABILITY_BROWSER_PLUGIN_NAME_RE = /^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/;
const CAPABILITY_BROWSER_REASON_CODE_RE = /^[a-z0-9][a-z0-9._-]*$/;
const REF_KEYS = ["kind", "id"];
const PRINCIPAL_KEYS = ["kind", "id"];
const SOURCE_KEYS = ["contractVersion", "sourceKind", "sourceId", "scope"];
const DEFINITION_KEYS = [
    "contractVersion",
    "definitionId",
    "assetKind",
    "owner",
    "source",
    "name",
    "description",
];
const DEPENDENCY_KEYS = ["kind", "refId", "version", "required"];
const SCHEMA_REF_KEYS = ["schemaId", "version", "schemaHash"];
const RELEASE_KEYS = [
    "contractVersion",
    "releaseId",
    "definitionId",
    "version",
    "status",
    "deliveryMode",
    "deployment",
    "executionKinds",
    "inputContractSummary",
    "outputContractSummary",
    "inputSchemaRef",
    "outputSchemaRef",
    "dependencyRefs",
    "contentHash",
    "minDosiaVersion",
    "createdAt",
    "publishedAt",
];
const GRANT_TARGET_KEYS = ["kind", "id"];
const GRANT_KEYS = [
    "contractVersion",
    "grantId",
    "target",
    "principal",
    "effect",
    "assignmentPolicy",
    "rights",
    "quotaPolicyRef",
    "validFrom",
    "validUntil",
];
const BINDING_TARGET_DISCRIMINATOR_KEYS = ["kind", "id", "principal"];
const PRINCIPAL_BINDING_TARGET_KEYS = ["kind", "principal"];
const ID_BINDING_TARGET_KEYS = ["kind", "id"];
const BINDING_KEYS = ["contractVersion", "bindingId", "target", "releaseId", "enabled", "configRefs"];
const READINESS_KEYS = ["status", "reasonCodes"];
const AVAILABILITY_KEYS = ["discover", "use"];
const SOURCE_COVERAGE_KEYS = ["sources", "readiness"];
const OBSERVED_CATALOG_KEYS = [
    "contractVersion",
    "catalogState",
    "catalogItemId",
    "definitionId",
    "assetKind",
    "source",
    "owner",
    "name",
    "description",
    "availability",
    "sourceCoverage",
];
const REGISTERED_CATALOG_KEYS = [
    ...OBSERVED_CATALOG_KEYS,
    "releaseId",
    "version",
    "status",
    "deliveryMode",
    "deployment",
    "executionKinds",
];
const SUBJECT_KEYS = ["kind", "id"];
const SNAPSHOT_RELEASE_REF_KEYS = ["releaseId", "version", "contentHash", "readiness"];
const SNAPSHOT_SKILL_REF_KEYS = ["skillId", "version", "contentHash", "readiness"];
const SNAPSHOT_MCP_REF_KEYS = ["serverId", "toolId", "version", "readiness"];
const SNAPSHOT_HOOK_REF_KEYS = ["hookId", "version", "contentHash", "readiness"];
const PERMISSION_SUMMARY_KEYS = ["granted", "denied", "approvalRequired"];
const SNAPSHOT_KEYS = [
    "contractVersion",
    "snapshotId",
    "snapshotHash",
    "subjectRef",
    "generatedAt",
    "releaseRefs",
    "skillRefs",
    "mcpRefs",
    "hookRefs",
    "permissionSummary",
    "readinessSummary",
];
const AUDIT_REF_KEYS = [
    "contractVersion",
    "auditRefId",
    "sourceKind",
    "recordId",
    "eventType",
    "definitionId",
    "releaseId",
    "occurredAt",
];
const SOURCE_SUMMARY_KEYS = [
    "sourceKind",
    "readiness",
    "itemCount",
    "provenanceOnlyCount",
    "updatedAt",
];
const CATALOG_LIST_KEYS = ["contractVersion", "generatedAt", "items", "sourceSummaries"];
const CATALOG_DETAIL_KEYS = ["contractVersion", "generatedAt", "item"];
const ORGANIZATION_AUDIENCE_KEYS = ["kind", "roleTemplateIds", "userIds"];
const ORGANIZATION_AUDIENCE_ORGANIZATION_KEYS = ["kind"];
const ORGANIZATION_AUDIENCE_ROLES_KEYS = ["kind", "roleTemplateIds"];
const ORGANIZATION_AUDIENCE_USERS_KEYS = ["kind", "userIds"];
const ORGANIZATION_CATALOG_ITEM_KEYS = ["item", "sourceAudience"];
const ORGANIZATION_CATALOG_LIST_KEYS = [
    "contractVersion",
    "organizationId",
    "generatedAt",
    "items",
    "sourceSummaries",
];
const ORGANIZATION_CATALOG_DETAIL_KEYS = [
    "contractVersion",
    "organizationId",
    "generatedAt",
    "item",
];
const ORGANIZATION_BROWSER_SOURCE_KEYS = ["kind", "pluginNames"];
const ORGANIZATION_BROWSER_AUDIENCE_ORGANIZATION_KEYS = ["kind"];
const ORGANIZATION_BROWSER_AUDIENCE_ROLES_KEYS = ["kind", "roleTemplateIds", "count"];
const ORGANIZATION_BROWSER_AUDIENCE_USERS_KEYS = ["kind", "count"];
const ORGANIZATION_BROWSER_ITEM_KEYS = [
    "contractVersion",
    "catalogItemId",
    "catalogState",
    "assetKind",
    "name",
    "description",
    "source",
    "sourceAudience",
    "sourceReadiness",
];
const ORGANIZATION_BROWSER_SOURCE_SUMMARY_KEYS = SOURCE_SUMMARY_KEYS;
const ORGANIZATION_BROWSER_LIST_KEYS = ORGANIZATION_CATALOG_LIST_KEYS;
const ORGANIZATION_BROWSER_DETAIL_KEYS = ORGANIZATION_CATALOG_DETAIL_KEYS;
const CATALOG_SUCCESS_RESPONSE_KEYS = ["success", "data"];
const CATALOG_ERROR_RESPONSE_KEYS = ["success", "error"];
const CATALOG_ERROR_BODY_KEYS = ["code", "message"];
function fail(path, message) {
    throw new CapabilityCenterContractError(path, message);
}
function isPlainRecord(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function strictObject(value, path, allowedKeys) {
    if (!isPlainRecord(value))
        fail(path, "must be a plain object");
    const allowed = new Set(allowedKeys);
    for (const key of Object.keys(value)) {
        if (PROTOTYPE_KEYS.has(key))
            fail(joinPath(path, key), "prototype-related field is forbidden");
        if (!allowed.has(key))
            fail(joinPath(path, key), "unknown field");
    }
    return value;
}
function joinPath(path, key) {
    return path ? `${path}.${key}` : key;
}
function indexedPath(path, index) {
    return `${path}[${index}]`;
}
function prepareRoot(value, keys) {
    return strictObject(value, "", keys);
}
function parseContractVersion(value, path) {
    if (value !== CAPABILITY_CENTER_CONTRACT_VERSION) {
        fail(path, `must be the safe integer ${CAPABILITY_CENTER_CONTRACT_VERSION}`);
    }
    return CAPABILITY_CENTER_CONTRACT_VERSION;
}
function containsPrivateLocalPath(value) {
    return value.includes("~/")
        || value.includes("/Users/")
        || value.includes("/home/")
        || value.includes("file://")
        || /[A-Za-z]:[\\/]/.test(value);
}
function boundedString(value, path, maxLength) {
    if (typeof value !== "string")
        fail(path, "must be a string");
    const normalized = value.trim();
    if (!normalized)
        fail(path, "must be a non-empty string");
    if (normalized.length > maxLength)
        fail(path, `must be at most ${maxLength} characters`);
    return normalized;
}
function opaqueRefString(value, path, maxLength) {
    if (typeof value === "string" && /[\u0000-\u001f\u007f]/.test(value)) {
        fail(path, "must not contain control characters");
    }
    const normalized = boundedString(value, path, maxLength);
    if (containsPrivateLocalPath(normalized))
        fail(path, "must not contain a private local path");
    return normalized;
}
function nullableOpaqueRefString(value, path, maxLength) {
    return value === null ? null : opaqueRefString(value, path, maxLength);
}
function sha256Digest(value, path) {
    const normalized = boundedString(value, path, CAPABILITY_CENTER_LIMITS.hashLength);
    if (!SHA256_DIGEST_RE.test(normalized)) {
        fail(path, "must be sha256:<64 lowercase hex characters>");
    }
    return normalized;
}
function booleanValue(value, path) {
    if (typeof value !== "boolean")
        fail(path, "must be a boolean");
    return value;
}
function enumValue(value, path, values) {
    if (typeof value === "string") {
        for (const option of values) {
            if (value === option)
                return option;
        }
    }
    fail(path, `must be one of: ${values.join(" | ")}`);
}
function isoTimestamp(value, path) {
    const timestamp = boundedString(value, path, 32);
    if (!NORMALIZED_ISO_UTC_RE.test(timestamp)) {
        fail(path, "must be a normalized ISO-8601 UTC timestamp with millisecond precision");
    }
    const milliseconds = Date.parse(timestamp);
    if (!Number.isFinite(milliseconds) || new Date(milliseconds).toISOString() !== timestamp) {
        fail(path, "must be a valid normalized ISO-8601 timestamp");
    }
    return timestamp;
}
function nullableIsoTimestamp(value, path) {
    return value === null ? null : isoTimestamp(value, path);
}
function stringArray(value, path, maxStringLength, minimumItems = 0) {
    if (!Array.isArray(value))
        fail(path, "must be an array");
    if (value.length < minimumItems)
        fail(path, `must contain at least ${minimumItems} item(s)`);
    if (value.length > CAPABILITY_CENTER_LIMITS.arrayItems) {
        fail(path, `must contain at most ${CAPABILITY_CENTER_LIMITS.arrayItems} items`);
    }
    const output = [];
    const seen = new Set();
    for (let index = 0; index < value.length; index += 1) {
        const item = boundedString(value[index], indexedPath(path, index), maxStringLength);
        if (seen.has(item))
            fail(indexedPath(path, index), `duplicate value "${item}"`);
        seen.add(item);
        output.push(item);
    }
    return output;
}
function opaqueRefArray(value, path, maxStringLength, minimumItems = 0, maximumItems = CAPABILITY_CENTER_LIMITS.arrayItems) {
    if (!Array.isArray(value))
        fail(path, "must be an array");
    if (value.length < minimumItems)
        fail(path, `must contain at least ${minimumItems} item(s)`);
    if (value.length > maximumItems) {
        fail(path, `must contain at most ${maximumItems} items`);
    }
    const output = [];
    const seen = new Set();
    for (let index = 0; index < value.length; index += 1) {
        const item = opaqueRefString(value[index], indexedPath(path, index), maxStringLength);
        if (seen.has(item))
            fail(indexedPath(path, index), `duplicate value "${item}"`);
        seen.add(item);
        output.push(item);
    }
    return output;
}
function objectArray(value, path, parser) {
    if (!Array.isArray(value))
        fail(path, "must be an array");
    if (value.length > CAPABILITY_CENTER_LIMITS.arrayItems) {
        fail(path, `must contain at most ${CAPABILITY_CENTER_LIMITS.arrayItems} items`);
    }
    return value.map((entry, index) => parser(entry, indexedPath(path, index)));
}
function assertUnique(entries, path, keyOf) {
    const seen = new Set();
    for (let index = 0; index < entries.length; index += 1) {
        const key = keyOf(entries[index]);
        if (seen.has(key))
            fail(indexedPath(path, index), `duplicate reference "${key}"`);
        seen.add(key);
    }
}
function parseNullablePlatformRef(value, path, kinds) {
    const object = strictObject(value, path, REF_KEYS);
    const kind = enumValue(object.kind, joinPath(path, "kind"), kinds);
    const id = nullableOpaqueRefString(object.id, joinPath(path, "id"), CAPABILITY_CENTER_LIMITS.idLength);
    if (kind === "platform" && id !== null)
        fail(joinPath(path, "id"), "must be null for platform scope");
    if (kind !== "platform" && id === null)
        fail(joinPath(path, "id"), `is required for ${kind} scope`);
    return { kind, id };
}
function parseSourceScope(value, path) {
    return parseNullablePlatformRef(value, path, CAPABILITY_SOURCE_SCOPE_KIND_VALUES);
}
function parseOwner(value, path) {
    return parseNullablePlatformRef(value, path, CAPABILITY_OWNER_KIND_VALUES);
}
function parsePrincipal(value, path) {
    const object = strictObject(value, path, PRINCIPAL_KEYS);
    return {
        kind: enumValue(object.kind, joinPath(path, "kind"), CAPABILITY_PRINCIPAL_KIND_VALUES),
        id: opaqueRefString(object.id, joinPath(path, "id"), CAPABILITY_CENTER_LIMITS.idLength),
    };
}
function parseSource(value, path) {
    const object = strictObject(value, path, SOURCE_KEYS);
    return {
        contractVersion: parseContractVersion(object.contractVersion, joinPath(path, "contractVersion")),
        sourceKind: boundedString(object.sourceKind, joinPath(path, "sourceKind"), CAPABILITY_CENTER_LIMITS.kindLength),
        sourceId: opaqueRefString(object.sourceId, joinPath(path, "sourceId"), CAPABILITY_CENTER_LIMITS.idLength),
        scope: parseSourceScope(object.scope, joinPath(path, "scope")),
    };
}
function parseDependency(value, path) {
    const object = strictObject(value, path, DEPENDENCY_KEYS);
    const version = nullableOpaqueRefString(object.version, joinPath(path, "version"), CAPABILITY_CENTER_LIMITS.versionLength);
    const required = booleanValue(object.required, joinPath(path, "required"));
    if (required && version === null)
        fail(joinPath(path, "version"), "is required for a required dependency");
    return {
        kind: boundedString(object.kind, joinPath(path, "kind"), CAPABILITY_CENTER_LIMITS.kindLength),
        refId: opaqueRefString(object.refId, joinPath(path, "refId"), CAPABILITY_CENTER_LIMITS.idLength),
        version,
        required,
    };
}
function parseSchemaRef(value, path) {
    const object = strictObject(value, path, SCHEMA_REF_KEYS);
    return {
        schemaId: opaqueRefString(object.schemaId, joinPath(path, "schemaId"), CAPABILITY_CENTER_LIMITS.idLength),
        version: opaqueRefString(object.version, joinPath(path, "version"), CAPABILITY_CENTER_LIMITS.versionLength),
        schemaHash: sha256Digest(object.schemaHash, joinPath(path, "schemaHash")),
    };
}
function assertDeliveryDeployment(deliveryMode, deployment, path) {
    const deploymentPath = joinPath(path, "deployment");
    if (deliveryMode === "local_editable" && deployment !== "device") {
        fail(deploymentPath, "must be device for local_editable delivery");
    }
    if (deliveryMode === "remote_protected" && deployment === "device") {
        fail(deploymentPath, "must be shared_cloud, private_runtime, or hybrid for remote_protected delivery");
    }
}
function parseExecutionKinds(value, path) {
    if (!Array.isArray(value))
        fail(path, "must be an array");
    if (value.length === 0)
        fail(path, "must contain at least 1 item");
    if (value.length > CAPABILITY_EXECUTION_KIND_VALUES.length) {
        fail(path, `must contain at most ${CAPABILITY_EXECUTION_KIND_VALUES.length} items`);
    }
    const output = [];
    const seen = new Set();
    for (let index = 0; index < value.length; index += 1) {
        const itemPath = indexedPath(path, index);
        const item = enumValue(value[index], itemPath, CAPABILITY_EXECUTION_KIND_VALUES);
        if (seen.has(item))
            fail(itemPath, `duplicate value "${item}"`);
        seen.add(item);
        output.push(item);
    }
    return output;
}
function parseGrantTarget(value, path) {
    const object = strictObject(value, path, GRANT_TARGET_KEYS);
    return {
        kind: enumValue(object.kind, joinPath(path, "kind"), ["definition", "release"]),
        id: opaqueRefString(object.id, joinPath(path, "id"), CAPABILITY_CENTER_LIMITS.idLength),
    };
}
function parseBindingTarget(value, path) {
    const object = strictObject(value, path, BINDING_TARGET_DISCRIMINATOR_KEYS);
    const kind = enumValue(object.kind, joinPath(path, "kind"), ["principal", "agent", "entry_point"]);
    if (kind === "principal") {
        const principalObject = strictObject(object, path, PRINCIPAL_BINDING_TARGET_KEYS);
        return { kind, principal: parsePrincipal(principalObject.principal, joinPath(path, "principal")) };
    }
    const idObject = strictObject(object, path, ID_BINDING_TARGET_KEYS);
    return {
        kind,
        id: opaqueRefString(idObject.id, joinPath(path, "id"), CAPABILITY_CENTER_LIMITS.idLength),
    };
}
function parseReadiness(value, path) {
    const object = strictObject(value, path, READINESS_KEYS);
    return {
        status: enumValue(object.status, joinPath(path, "status"), CAPABILITY_READINESS_STATUS_VALUES),
        reasonCodes: stringArray(object.reasonCodes, joinPath(path, "reasonCodes"), CAPABILITY_CENTER_LIMITS.reasonCodeLength),
    };
}
function parseAvailability(value, path) {
    const object = strictObject(value, path, AVAILABILITY_KEYS);
    return {
        discover: booleanValue(object.discover, joinPath(path, "discover")),
        use: booleanValue(object.use, joinPath(path, "use")),
    };
}
function parseSourceCoverage(value, path) {
    const object = strictObject(value, path, SOURCE_COVERAGE_KEYS);
    return {
        sources: stringArray(object.sources, joinPath(path, "sources"), CAPABILITY_CENTER_LIMITS.kindLength, 1),
        readiness: parseReadiness(object.readiness, joinPath(path, "readiness")),
    };
}
function parseSubject(value, path) {
    const object = strictObject(value, path, SUBJECT_KEYS);
    return {
        kind: enumValue(object.kind, joinPath(path, "kind"), CAPABILITY_SUBJECT_KIND_VALUES),
        id: opaqueRefString(object.id, joinPath(path, "id"), CAPABILITY_CENTER_LIMITS.idLength),
    };
}
function parseSnapshotReleaseRef(value, path) {
    const object = strictObject(value, path, SNAPSHOT_RELEASE_REF_KEYS);
    return {
        releaseId: opaqueRefString(object.releaseId, joinPath(path, "releaseId"), CAPABILITY_CENTER_LIMITS.idLength),
        version: boundedString(object.version, joinPath(path, "version"), CAPABILITY_CENTER_LIMITS.versionLength),
        contentHash: sha256Digest(object.contentHash, joinPath(path, "contentHash")),
        readiness: parseReadiness(object.readiness, joinPath(path, "readiness")),
    };
}
function parseSnapshotSkillRef(value, path) {
    const object = strictObject(value, path, SNAPSHOT_SKILL_REF_KEYS);
    return {
        skillId: opaqueRefString(object.skillId, joinPath(path, "skillId"), CAPABILITY_CENTER_LIMITS.idLength),
        version: boundedString(object.version, joinPath(path, "version"), CAPABILITY_CENTER_LIMITS.versionLength),
        contentHash: sha256Digest(object.contentHash, joinPath(path, "contentHash")),
        readiness: parseReadiness(object.readiness, joinPath(path, "readiness")),
    };
}
function parseSnapshotMcpRef(value, path) {
    const object = strictObject(value, path, SNAPSHOT_MCP_REF_KEYS);
    return {
        serverId: opaqueRefString(object.serverId, joinPath(path, "serverId"), CAPABILITY_CENTER_LIMITS.idLength),
        toolId: opaqueRefString(object.toolId, joinPath(path, "toolId"), CAPABILITY_CENTER_LIMITS.idLength),
        version: boundedString(object.version, joinPath(path, "version"), CAPABILITY_CENTER_LIMITS.versionLength),
        readiness: parseReadiness(object.readiness, joinPath(path, "readiness")),
    };
}
function parseSnapshotHookRef(value, path) {
    const object = strictObject(value, path, SNAPSHOT_HOOK_REF_KEYS);
    return {
        hookId: opaqueRefString(object.hookId, joinPath(path, "hookId"), CAPABILITY_CENTER_LIMITS.idLength),
        version: boundedString(object.version, joinPath(path, "version"), CAPABILITY_CENTER_LIMITS.versionLength),
        contentHash: sha256Digest(object.contentHash, joinPath(path, "contentHash")),
        readiness: parseReadiness(object.readiness, joinPath(path, "readiness")),
    };
}
function parsePermissionSummary(value, path) {
    const object = strictObject(value, path, PERMISSION_SUMMARY_KEYS);
    const granted = stringArray(object.granted, joinPath(path, "granted"), CAPABILITY_CENTER_LIMITS.actionLength);
    const denied = stringArray(object.denied, joinPath(path, "denied"), CAPABILITY_CENTER_LIMITS.actionLength);
    const approvalRequired = stringArray(object.approvalRequired, joinPath(path, "approvalRequired"), CAPABILITY_CENTER_LIMITS.actionLength);
    const permissionSets = [granted, denied, approvalRequired];
    const seen = new Set();
    for (const permissionSet of permissionSets) {
        for (const action of permissionSet) {
            if (seen.has(action))
                fail(path, `action "${action}" appears in more than one permission set`);
            seen.add(action);
        }
    }
    return { granted, denied, approvalRequired };
}
/** Canonical builder/parser for stable source provenance. */
export function parseCapabilitySourceRefV1(raw) {
    const object = prepareRoot(raw, SOURCE_KEYS);
    return parseSource(object, "");
}
/** Canonical builder/parser for a stable capability definition. */
export function parseCapabilityDefinitionV1(raw) {
    const object = prepareRoot(raw, DEFINITION_KEYS);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        definitionId: opaqueRefString(object.definitionId, "definitionId", CAPABILITY_CENTER_LIMITS.idLength),
        assetKind: enumValue(object.assetKind, "assetKind", CAPABILITY_ASSET_KIND_VALUES),
        owner: parseOwner(object.owner, "owner"),
        source: parseSource(object.source, "source"),
        name: boundedString(object.name, "name", CAPABILITY_CENTER_LIMITS.nameLength),
        description: boundedString(object.description, "description", CAPABILITY_CENTER_LIMITS.descriptionLength),
    };
}
/** Canonical builder/parser for an immutable capability release descriptor. */
export function parseCapabilityReleaseV1(raw) {
    const object = prepareRoot(raw, RELEASE_KEYS);
    const status = enumValue(object.status, "status", CAPABILITY_RELEASE_STATUS_VALUES);
    const deliveryMode = enumValue(object.deliveryMode, "deliveryMode", CAPABILITY_DELIVERY_MODE_VALUES);
    const deployment = enumValue(object.deployment, "deployment", CAPABILITY_DEPLOYMENT_VALUES);
    assertDeliveryDeployment(deliveryMode, deployment, "");
    const publishedAt = nullableIsoTimestamp(object.publishedAt, "publishedAt");
    if (status === "published" && publishedAt === null)
        fail("publishedAt", "is required for published status");
    if ((status === "draft" || status === "validating") && publishedAt !== null) {
        fail("publishedAt", `must be null for ${status} status`);
    }
    const dependencyRefs = objectArray(object.dependencyRefs, "dependencyRefs", parseDependency);
    assertUnique(dependencyRefs, "dependencyRefs", (entry) => `${entry.kind}\u0000${entry.refId}\u0000${entry.version ?? ""}`);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        releaseId: opaqueRefString(object.releaseId, "releaseId", CAPABILITY_CENTER_LIMITS.idLength),
        definitionId: opaqueRefString(object.definitionId, "definitionId", CAPABILITY_CENTER_LIMITS.idLength),
        version: boundedString(object.version, "version", CAPABILITY_CENTER_LIMITS.versionLength),
        status,
        deliveryMode,
        deployment,
        executionKinds: parseExecutionKinds(object.executionKinds, "executionKinds"),
        inputContractSummary: boundedString(object.inputContractSummary, "inputContractSummary", CAPABILITY_CENTER_LIMITS.summaryLength),
        outputContractSummary: boundedString(object.outputContractSummary, "outputContractSummary", CAPABILITY_CENTER_LIMITS.summaryLength),
        inputSchemaRef: parseSchemaRef(object.inputSchemaRef, "inputSchemaRef"),
        outputSchemaRef: parseSchemaRef(object.outputSchemaRef, "outputSchemaRef"),
        dependencyRefs,
        contentHash: sha256Digest(object.contentHash, "contentHash"),
        minDosiaVersion: opaqueRefString(object.minDosiaVersion, "minDosiaVersion", CAPABILITY_CENTER_LIMITS.versionLength),
        createdAt: isoTimestamp(object.createdAt, "createdAt"),
        publishedAt,
    };
}
/** Canonical builder/parser for entitlement grants. */
export function parseCapabilityGrantV1(raw) {
    const object = prepareRoot(raw, GRANT_KEYS);
    const rightsRaw = stringArray(object.rights, "rights", CAPABILITY_CENTER_LIMITS.kindLength, 1);
    const rights = rightsRaw.map((right, index) => enumValue(right, indexedPath("rights", index), CAPABILITY_RIGHT_VALUES));
    const validFrom = isoTimestamp(object.validFrom, "validFrom");
    const validUntil = nullableIsoTimestamp(object.validUntil, "validUntil");
    if (validUntil !== null && validUntil <= validFrom)
        fail("validUntil", "must be strictly later than validFrom");
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        grantId: opaqueRefString(object.grantId, "grantId", CAPABILITY_CENTER_LIMITS.idLength),
        target: parseGrantTarget(object.target, "target"),
        principal: parsePrincipal(object.principal, "principal"),
        effect: enumValue(object.effect, "effect", CAPABILITY_GRANT_EFFECT_VALUES),
        assignmentPolicy: object.assignmentPolicy === null
            ? null
            : enumValue(object.assignmentPolicy, "assignmentPolicy", CAPABILITY_ASSIGNMENT_POLICY_VALUES),
        rights,
        quotaPolicyRef: nullableOpaqueRefString(object.quotaPolicyRef, "quotaPolicyRef", CAPABILITY_CENTER_LIMITS.idLength),
        validFrom,
        validUntil,
    };
}
/** Canonical builder/parser for configuration/entitlement bindings. */
export function parseCapabilityBindingV1(raw) {
    const object = prepareRoot(raw, BINDING_KEYS);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        bindingId: opaqueRefString(object.bindingId, "bindingId", CAPABILITY_CENTER_LIMITS.idLength),
        target: parseBindingTarget(object.target, "target"),
        releaseId: opaqueRefString(object.releaseId, "releaseId", CAPABILITY_CENTER_LIMITS.idLength),
        enabled: booleanValue(object.enabled, "enabled"),
        configRefs: opaqueRefArray(object.configRefs, "configRefs", CAPABILITY_CENTER_LIMITS.idLength),
    };
}
/** Canonical builder/parser for non-secret directory summaries. */
export function parseCapabilityCatalogItemV1(raw) {
    const object = prepareRoot(raw, REGISTERED_CATALOG_KEYS);
    const catalogState = enumValue(object.catalogState, "catalogState", ["observed", "registered"]);
    const common = {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        catalogItemId: opaqueRefString(object.catalogItemId, "catalogItemId", CAPABILITY_CENTER_LIMITS.idLength),
        assetKind: enumValue(object.assetKind, "assetKind", CAPABILITY_ASSET_KIND_VALUES),
        source: parseSource(object.source, "source"),
        owner: parseOwner(object.owner, "owner"),
        name: boundedString(object.name, "name", CAPABILITY_CENTER_LIMITS.nameLength),
        description: boundedString(object.description, "description", CAPABILITY_CENTER_LIMITS.descriptionLength),
        availability: parseAvailability(object.availability, "availability"),
        sourceCoverage: parseSourceCoverage(object.sourceCoverage, "sourceCoverage"),
    };
    if (catalogState === "observed") {
        strictObject(object, "", OBSERVED_CATALOG_KEYS);
        if (object.definitionId !== null)
            fail("definitionId", "must be null for observed catalog state");
        return { ...common, catalogState, definitionId: null };
    }
    strictObject(object, "", REGISTERED_CATALOG_KEYS);
    const deliveryMode = enumValue(object.deliveryMode, "deliveryMode", CAPABILITY_DELIVERY_MODE_VALUES);
    const deployment = enumValue(object.deployment, "deployment", CAPABILITY_DEPLOYMENT_VALUES);
    assertDeliveryDeployment(deliveryMode, deployment, "");
    return {
        ...common,
        catalogState,
        definitionId: opaqueRefString(object.definitionId, "definitionId", CAPABILITY_CENTER_LIMITS.idLength),
        releaseId: opaqueRefString(object.releaseId, "releaseId", CAPABILITY_CENTER_LIMITS.idLength),
        version: boundedString(object.version, "version", CAPABILITY_CENTER_LIMITS.versionLength),
        status: enumValue(object.status, "status", CAPABILITY_RELEASE_STATUS_VALUES),
        deliveryMode,
        deployment,
        executionKinds: parseExecutionKinds(object.executionKinds, "executionKinds"),
    };
}
/** Canonical builder/parser for non-secret Continuity snapshot references. */
export function parseCapabilitySnapshotV1(raw) {
    const object = prepareRoot(raw, SNAPSHOT_KEYS);
    const releaseRefs = objectArray(object.releaseRefs, "releaseRefs", parseSnapshotReleaseRef);
    const skillRefs = objectArray(object.skillRefs, "skillRefs", parseSnapshotSkillRef);
    const mcpRefs = objectArray(object.mcpRefs, "mcpRefs", parseSnapshotMcpRef);
    const hookRefs = objectArray(object.hookRefs, "hookRefs", parseSnapshotHookRef);
    assertUnique(releaseRefs, "releaseRefs", (entry) => entry.releaseId);
    assertUnique(skillRefs, "skillRefs", (entry) => entry.skillId);
    assertUnique(mcpRefs, "mcpRefs", (entry) => `${entry.serverId}\u0000${entry.toolId}`);
    assertUnique(hookRefs, "hookRefs", (entry) => entry.hookId);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        snapshotId: opaqueRefString(object.snapshotId, "snapshotId", CAPABILITY_CENTER_LIMITS.idLength),
        snapshotHash: sha256Digest(object.snapshotHash, "snapshotHash"),
        subjectRef: parseSubject(object.subjectRef, "subjectRef"),
        generatedAt: isoTimestamp(object.generatedAt, "generatedAt"),
        releaseRefs,
        skillRefs,
        mcpRefs,
        hookRefs,
        permissionSummary: parsePermissionSummary(object.permissionSummary, "permissionSummary"),
        readinessSummary: parseReadiness(object.readinessSummary, "readinessSummary"),
    };
}
/** Canonical builder/parser for stable governance/audit references only. */
export function parseCapabilityAuditRefV1(raw) {
    const object = prepareRoot(raw, AUDIT_REF_KEYS);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        auditRefId: opaqueRefString(object.auditRefId, "auditRefId", CAPABILITY_CENTER_LIMITS.idLength),
        sourceKind: boundedString(object.sourceKind, "sourceKind", CAPABILITY_CENTER_LIMITS.kindLength),
        recordId: opaqueRefString(object.recordId, "recordId", CAPABILITY_CENTER_LIMITS.idLength),
        eventType: boundedString(object.eventType, "eventType", CAPABILITY_CENTER_LIMITS.kindLength),
        definitionId: opaqueRefString(object.definitionId, "definitionId", CAPABILITY_CENTER_LIMITS.idLength),
        releaseId: nullableOpaqueRefString(object.releaseId, "releaseId", CAPABILITY_CENTER_LIMITS.idLength),
        occurredAt: isoTimestamp(object.occurredAt, "occurredAt"),
    };
}
function nonNegativeSafeInteger(value, path) {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
        fail(path, "must be a non-negative safe integer");
    }
    return value;
}
function parseSourceSummary(value, path) {
    const object = strictObject(value, path, SOURCE_SUMMARY_KEYS);
    return {
        sourceKind: boundedString(object.sourceKind, joinPath(path, "sourceKind"), CAPABILITY_CENTER_LIMITS.kindLength),
        readiness: parseReadiness(object.readiness, joinPath(path, "readiness")),
        itemCount: nonNegativeSafeInteger(object.itemCount, joinPath(path, "itemCount")),
        provenanceOnlyCount: nonNegativeSafeInteger(object.provenanceOnlyCount, joinPath(path, "provenanceOnlyCount")),
        updatedAt: isoTimestamp(object.updatedAt, joinPath(path, "updatedAt")),
    };
}
function parseCatalogItemAt(value, path) {
    try {
        return parseCapabilityCatalogItemV1(value);
    }
    catch (error) {
        if (error instanceof CapabilityCenterContractError) {
            const nestedPath = error.path ? joinPath(path, error.path) : path;
            const message = error.path && error.message.startsWith(`${error.path}: `)
                ? error.message.slice(error.path.length + 2)
                : error.message;
            fail(nestedPath, message);
        }
        throw error;
    }
}
function parseOrganizationSourceAudience(value, path) {
    const object = strictObject(value, path, ORGANIZATION_AUDIENCE_KEYS);
    const kind = enumValue(object.kind, joinPath(path, "kind"), ["organization", "roles", "users"]);
    if (kind === "organization") {
        strictObject(object, path, ORGANIZATION_AUDIENCE_ORGANIZATION_KEYS);
        return { kind };
    }
    if (kind === "roles") {
        strictObject(object, path, ORGANIZATION_AUDIENCE_ROLES_KEYS);
        return {
            kind,
            roleTemplateIds: opaqueRefArray(object.roleTemplateIds, joinPath(path, "roleTemplateIds"), CAPABILITY_CENTER_LIMITS.idLength, 1, CAPABILITY_CENTER_LIMITS.organizationAudienceRefsPerItem),
        };
    }
    strictObject(object, path, ORGANIZATION_AUDIENCE_USERS_KEYS);
    return {
        kind,
        userIds: opaqueRefArray(object.userIds, joinPath(path, "userIds"), CAPABILITY_CENTER_LIMITS.idLength, 1, CAPABILITY_CENTER_LIMITS.organizationAudienceRefsPerItem),
    };
}
/** Canonical builder/parser for a legacy enterprise source audience (not an effective ACL). */
export function parseOrganizationCapabilitySourceAudienceV1(raw) {
    return parseOrganizationSourceAudience(raw, "");
}
/** Canonical builder/parser for one enterprise catalog wrapper. */
export function parseOrganizationCapabilityCatalogItemV1(raw) {
    const object = prepareRoot(raw, ORGANIZATION_CATALOG_ITEM_KEYS);
    return {
        item: parseCatalogItemAt(object.item, "item"),
        sourceAudience: parseOrganizationSourceAudience(object.sourceAudience, "sourceAudience"),
    };
}
function parseOrganizationCatalogItemAt(value, path) {
    try {
        return parseOrganizationCapabilityCatalogItemV1(value);
    }
    catch (error) {
        if (error instanceof CapabilityCenterContractError) {
            const nestedPath = error.path ? joinPath(path, error.path) : path;
            const message = error.path && error.message.startsWith(`${error.path}: `)
                ? error.message.slice(error.path.length + 2)
                : error.message;
            fail(nestedPath, message);
        }
        throw error;
    }
}
function organizationAudienceRefCount(audience) {
    if (audience.kind === "roles")
        return audience.roleTemplateIds.length;
    if (audience.kind === "users")
        return audience.userIds.length;
    return 0;
}
function assertOrganizationAudiencePayloadLimit(items, path) {
    let count = 0;
    for (let index = 0; index < items.length; index += 1) {
        count += organizationAudienceRefCount(items[index].sourceAudience);
        if (count > CAPABILITY_CENTER_LIMITS.organizationAudienceRefsPerPayload) {
            fail(indexedPath(path, index), `audience refs must contain at most ${CAPABILITY_CENTER_LIMITS.organizationAudienceRefsPerPayload} items per payload`);
        }
    }
}
function assertOrganizationCatalogItemMatchesRoot(wrapper, organizationId, path) {
    const { item } = wrapper;
    if (item.source.scope.kind !== "platform" && item.source.scope.kind !== "organization") {
        fail(joinPath(path, "item.source.scope.kind"), "must be platform or organization");
    }
    if (item.source.scope.kind === "organization" && item.source.scope.id !== organizationId) {
        fail(joinPath(path, "item.source.scope.id"), "must equal root organizationId");
    }
    if (item.owner.kind !== "platform" && item.owner.kind !== "organization") {
        fail(joinPath(path, "item.owner.kind"), "must be platform or organization");
    }
    if (item.owner.kind === "organization" && item.owner.id !== organizationId) {
        fail(joinPath(path, "item.owner.id"), "must equal root organizationId");
    }
}
/** Canonical builder/parser for a non-secret capability catalog list. */
export function parseCapabilityCatalogListV1(raw) {
    const object = prepareRoot(raw, CATALOG_LIST_KEYS);
    if (!Array.isArray(object.items))
        fail("items", "must be an array");
    if (object.items.length > CAPABILITY_CENTER_LIMITS.catalogListItems) {
        fail("items", `must contain at most ${CAPABILITY_CENTER_LIMITS.catalogListItems} items`);
    }
    const items = object.items.map((entry, index) => parseCatalogItemAt(entry, indexedPath("items", index)));
    assertUnique(items, "items", (entry) => entry.catalogItemId);
    if (!Array.isArray(object.sourceSummaries))
        fail("sourceSummaries", "must be an array");
    if (object.sourceSummaries.length > CAPABILITY_CENTER_LIMITS.catalogSourceSummaries) {
        fail("sourceSummaries", `must contain at most ${CAPABILITY_CENTER_LIMITS.catalogSourceSummaries} items`);
    }
    const sourceSummaries = object.sourceSummaries.map((entry, index) => parseSourceSummary(entry, indexedPath("sourceSummaries", index)));
    assertUnique(sourceSummaries, "sourceSummaries", (entry) => entry.sourceKind);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        generatedAt: isoTimestamp(object.generatedAt, "generatedAt"),
        items,
        sourceSummaries,
    };
}
/** Canonical builder/parser for a non-secret capability catalog detail. */
export function parseCapabilityCatalogDetailV1(raw) {
    const object = prepareRoot(raw, CATALOG_DETAIL_KEYS);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        generatedAt: isoTimestamp(object.generatedAt, "generatedAt"),
        item: parseCatalogItemAt(object.item, "item"),
    };
}
/** Canonical builder/parser for a successful catalog list HTTP envelope. */
export function parseCapabilityCatalogListResponseV1(raw) {
    const object = prepareRoot(raw, CATALOG_SUCCESS_RESPONSE_KEYS);
    if (object.success !== true)
        fail("success", "must be true");
    return {
        success: true,
        data: parseCapabilityCatalogListV1(object.data),
    };
}
/** Canonical builder/parser for a successful catalog detail HTTP envelope. */
export function parseCapabilityCatalogDetailResponseV1(raw) {
    const object = prepareRoot(raw, CATALOG_SUCCESS_RESPONSE_KEYS);
    if (object.success !== true)
        fail("success", "must be true");
    return {
        success: true,
        data: parseCapabilityCatalogDetailV1(object.data),
    };
}
/** Canonical builder/parser for a non-secret enterprise capability catalog list. */
export function parseOrganizationCapabilityCatalogListV1(raw) {
    const object = prepareRoot(raw, ORGANIZATION_CATALOG_LIST_KEYS);
    const organizationId = opaqueRefString(object.organizationId, "organizationId", CAPABILITY_CENTER_LIMITS.idLength);
    if (!Array.isArray(object.items))
        fail("items", "must be an array");
    if (object.items.length > CAPABILITY_CENTER_LIMITS.catalogListItems) {
        fail("items", `must contain at most ${CAPABILITY_CENTER_LIMITS.catalogListItems} items`);
    }
    const items = object.items.map((entry, index) => parseOrganizationCatalogItemAt(entry, indexedPath("items", index)));
    assertUnique(items, "items", (entry) => entry.item.catalogItemId);
    assertOrganizationAudiencePayloadLimit(items, "items");
    for (let index = 0; index < items.length; index += 1) {
        assertOrganizationCatalogItemMatchesRoot(items[index], organizationId, indexedPath("items", index));
    }
    if (!Array.isArray(object.sourceSummaries))
        fail("sourceSummaries", "must be an array");
    if (object.sourceSummaries.length > CAPABILITY_CENTER_LIMITS.catalogSourceSummaries) {
        fail("sourceSummaries", `must contain at most ${CAPABILITY_CENTER_LIMITS.catalogSourceSummaries} items`);
    }
    const sourceSummaries = object.sourceSummaries.map((entry, index) => parseSourceSummary(entry, indexedPath("sourceSummaries", index)));
    assertUnique(sourceSummaries, "sourceSummaries", (entry) => entry.sourceKind);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        organizationId,
        generatedAt: isoTimestamp(object.generatedAt, "generatedAt"),
        items,
        sourceSummaries,
    };
}
/** Canonical builder/parser for one non-secret enterprise capability catalog item. */
export function parseOrganizationCapabilityCatalogDetailV1(raw) {
    const object = prepareRoot(raw, ORGANIZATION_CATALOG_DETAIL_KEYS);
    const organizationId = opaqueRefString(object.organizationId, "organizationId", CAPABILITY_CENTER_LIMITS.idLength);
    const item = parseOrganizationCatalogItemAt(object.item, "item");
    assertOrganizationAudiencePayloadLimit([item], "item");
    assertOrganizationCatalogItemMatchesRoot(item, organizationId, "item");
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        organizationId,
        generatedAt: isoTimestamp(object.generatedAt, "generatedAt"),
        item,
    };
}
/** Canonical builder/parser for a successful enterprise catalog list HTTP envelope. */
export function parseOrganizationCapabilityCatalogListResponseV1(raw) {
    const object = prepareRoot(raw, CATALOG_SUCCESS_RESPONSE_KEYS);
    if (object.success !== true)
        fail("success", "must be true");
    return {
        success: true,
        data: parseOrganizationCapabilityCatalogListV1(object.data),
    };
}
/** Canonical builder/parser for a successful enterprise catalog detail HTTP envelope. */
export function parseOrganizationCapabilityCatalogDetailResponseV1(raw) {
    const object = prepareRoot(raw, CATALOG_SUCCESS_RESPONSE_KEYS);
    if (object.success !== true)
        fail("success", "must be true");
    return {
        success: true,
        data: parseOrganizationCapabilityCatalogDetailV1(object.data),
    };
}
function parseOrganizationCapabilityBrowserAudienceAt(raw, path) {
    const discriminator = strictObject(raw, path, ["kind", "roleTemplateIds", "count"]);
    const kind = enumValue(discriminator.kind, joinPath(path, "kind"), ["organization", "roles", "users"]);
    if (kind === "organization") {
        strictObject(raw, path, ORGANIZATION_BROWSER_AUDIENCE_ORGANIZATION_KEYS);
        return { kind };
    }
    if (kind === "roles") {
        const object = strictObject(raw, path, ORGANIZATION_BROWSER_AUDIENCE_ROLES_KEYS);
        const roleTemplateIds = opaqueRefArray(object.roleTemplateIds, joinPath(path, "roleTemplateIds"), CAPABILITY_CENTER_LIMITS.idLength, 1, CAPABILITY_CENTER_LIMITS.organizationAudienceRefsPerItem);
        assertCodeUnitSorted(roleTemplateIds, joinPath(path, "roleTemplateIds"));
        const count = nonNegativeSafeInteger(object.count, joinPath(path, "count"));
        if (count !== roleTemplateIds.length)
            fail(joinPath(path, "count"), "must equal roleTemplateIds length");
        return { kind, roleTemplateIds, count };
    }
    const object = strictObject(raw, path, ORGANIZATION_BROWSER_AUDIENCE_USERS_KEYS);
    const count = nonNegativeSafeInteger(object.count, joinPath(path, "count"));
    if (count === 0)
        fail(joinPath(path, "count"), "must be greater than zero");
    return { kind, count };
}
function assertCodeUnitSorted(values, path) {
    for (let index = 1; index < values.length; index += 1) {
        if (values[index] < values[index - 1]) {
            fail(indexedPath(path, index), "must be sorted in ascending code-unit order");
        }
    }
}
function parseOrganizationCapabilityBrowserReadinessAt(raw, path) {
    const readiness = parseReadiness(raw, path);
    for (let index = 0; index < readiness.reasonCodes.length; index += 1) {
        if (!CAPABILITY_BROWSER_REASON_CODE_RE.test(readiness.reasonCodes[index])) {
            fail(indexedPath(joinPath(path, "reasonCodes"), index), "must be a lowercase safe machine code");
        }
    }
    assertCodeUnitSorted(readiness.reasonCodes, joinPath(path, "reasonCodes"));
    return readiness;
}
function parseOrganizationCapabilityBrowserItemAt(raw, path) {
    const object = strictObject(raw, path, ORGANIZATION_BROWSER_ITEM_KEYS);
    const sourceObject = strictObject(object.source, joinPath(path, "source"), ORGANIZATION_BROWSER_SOURCE_KEYS);
    const assetKind = enumValue(object.assetKind, joinPath(path, "assetKind"), ORGANIZATION_CAPABILITY_BROWSER_ASSET_KIND_VALUES);
    const sourceKind = enumValue(sourceObject.kind, joinPath(path, "source.kind"), ORGANIZATION_CAPABILITY_BROWSER_SOURCE_KIND_VALUES);
    if ((assetKind === "skill" && sourceKind !== "enterprise_skill")
        || (assetKind === "agent" && sourceKind !== "enterprise_agent")
        || (assetKind === "job_template" && sourceKind !== "enterprise_task_definition")) {
        fail(joinPath(path, "source.kind"), `does not match assetKind ${assetKind}`);
    }
    const pluginNames = stringArray(sourceObject.pluginNames, joinPath(path, "source.pluginNames"), CAPABILITY_CENTER_LIMITS.kindLength);
    for (let index = 0; index < pluginNames.length; index += 1) {
        if (!CAPABILITY_BROWSER_PLUGIN_NAME_RE.test(pluginNames[index])) {
            fail(indexedPath(joinPath(path, "source.pluginNames"), index), "must be a safe plugin name");
        }
    }
    assertCodeUnitSorted(pluginNames, joinPath(path, "source.pluginNames"));
    return {
        contractVersion: parseContractVersion(object.contractVersion, joinPath(path, "contractVersion")),
        catalogItemId: opaqueRefString(object.catalogItemId, joinPath(path, "catalogItemId"), CAPABILITY_CENTER_LIMITS.idLength),
        catalogState: enumValue(object.catalogState, joinPath(path, "catalogState"), ["observed", "registered"]),
        assetKind,
        name: boundedString(object.name, joinPath(path, "name"), CAPABILITY_CENTER_LIMITS.nameLength),
        description: boundedString(object.description, joinPath(path, "description"), CAPABILITY_CENTER_LIMITS.descriptionLength),
        source: {
            kind: sourceKind,
            pluginNames,
        },
        sourceAudience: parseOrganizationCapabilityBrowserAudienceAt(object.sourceAudience, joinPath(path, "sourceAudience")),
        sourceReadiness: parseOrganizationCapabilityBrowserReadinessAt(object.sourceReadiness, joinPath(path, "sourceReadiness")),
    };
}
function parseOrganizationCapabilityBrowserSourceSummaryAt(raw, path) {
    const object = strictObject(raw, path, ORGANIZATION_BROWSER_SOURCE_SUMMARY_KEYS);
    return {
        sourceKind: enumValue(object.sourceKind, joinPath(path, "sourceKind"), ["enterprise_files", "enterprise_tasks_v1", "enterprise_tasks_v2"]),
        readiness: parseOrganizationCapabilityBrowserReadinessAt(object.readiness, joinPath(path, "readiness")),
        itemCount: nonNegativeSafeInteger(object.itemCount, joinPath(path, "itemCount")),
        provenanceOnlyCount: nonNegativeSafeInteger(object.provenanceOnlyCount, joinPath(path, "provenanceOnlyCount")),
        updatedAt: isoTimestamp(object.updatedAt, joinPath(path, "updatedAt")),
    };
}
export function parseOrganizationCapabilityBrowserListV1(raw) {
    const object = prepareRoot(raw, ORGANIZATION_BROWSER_LIST_KEYS);
    if (!Array.isArray(object.items))
        fail("items", "must be an array");
    if (object.items.length > CAPABILITY_CENTER_LIMITS.catalogListItems) {
        fail("items", `must contain at most ${CAPABILITY_CENTER_LIMITS.catalogListItems} items`);
    }
    const items = object.items.map((entry, index) => parseOrganizationCapabilityBrowserItemAt(entry, indexedPath("items", index)));
    assertUnique(items, "items", (entry) => entry.catalogItemId);
    if (!Array.isArray(object.sourceSummaries))
        fail("sourceSummaries", "must be an array");
    if (object.sourceSummaries.length > CAPABILITY_CENTER_LIMITS.catalogSourceSummaries) {
        fail("sourceSummaries", `must contain at most ${CAPABILITY_CENTER_LIMITS.catalogSourceSummaries} items`);
    }
    const sourceSummaries = object.sourceSummaries.map((entry, index) => parseOrganizationCapabilityBrowserSourceSummaryAt(entry, indexedPath("sourceSummaries", index)));
    assertUnique(sourceSummaries, "sourceSummaries", (entry) => entry.sourceKind);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        organizationId: opaqueRefString(object.organizationId, "organizationId", CAPABILITY_CENTER_LIMITS.idLength),
        generatedAt: isoTimestamp(object.generatedAt, "generatedAt"),
        items,
        sourceSummaries,
    };
}
export function parseOrganizationCapabilityBrowserDetailV1(raw) {
    const object = prepareRoot(raw, ORGANIZATION_BROWSER_DETAIL_KEYS);
    return {
        contractVersion: parseContractVersion(object.contractVersion, "contractVersion"),
        organizationId: opaqueRefString(object.organizationId, "organizationId", CAPABILITY_CENTER_LIMITS.idLength),
        generatedAt: isoTimestamp(object.generatedAt, "generatedAt"),
        item: parseOrganizationCapabilityBrowserItemAt(object.item, "item"),
    };
}
export function parseOrganizationCapabilityBrowserListResponseV1(raw) {
    const object = prepareRoot(raw, CATALOG_SUCCESS_RESPONSE_KEYS);
    if (object.success !== true)
        fail("success", "must be true");
    return { success: true, data: parseOrganizationCapabilityBrowserListV1(object.data) };
}
export function parseOrganizationCapabilityBrowserDetailResponseV1(raw) {
    const object = prepareRoot(raw, CATALOG_SUCCESS_RESPONSE_KEYS);
    if (object.success !== true)
        fail("success", "must be true");
    return { success: true, data: parseOrganizationCapabilityBrowserDetailV1(object.data) };
}
/** Canonical builder/parser for a safe catalog error HTTP envelope. */
export function parseCapabilityCatalogErrorResponseV1(raw) {
    const object = prepareRoot(raw, CATALOG_ERROR_RESPONSE_KEYS);
    if (object.success !== false)
        fail("success", "must be false");
    const error = strictObject(object.error, "error", CATALOG_ERROR_BODY_KEYS);
    return {
        success: false,
        error: {
            code: boundedString(error.code, "error.code", CAPABILITY_CENTER_LIMITS.errorCodeLength),
            message: boundedString(error.message, "error.message", CAPABILITY_CENTER_LIMITS.errorMessageLength),
        },
    };
}
//# sourceMappingURL=capability-center-contract.js.map